import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { 
  FiFileText, 
  FiTrendingUp, 
  FiShield,
  FiArrowRight,
  FiPlay,
  FiCheckCircle,
  FiZap,
  FiDollarSign,
  FiActivity,
} from 'react-icons/fi';
import { ThemeContext } from '../context/ThemeContext';
import MarketingLayout from '../layouts/MarketingLayout';
import Spotlight from '../components/Spotlight';
import SpotlightCard from '../components/SpotlightCard';
import ProductPreview from '../components/marketing/ProductPreview';
import DesktopAppInstallCta from '../components/marketing/DesktopAppInstallCta';

const Landing = () => {
  const [activeSection, setActiveSection] = useState(0);
  const { modoOscuro } = useContext(ThemeContext);
  const [isLg, setIsLg] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => setIsLg(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  
  // Navbar visible solo en hero (desktop)
  const showNavbar = activeSection === 0;
  
  // Refs para secciones
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const featuresRef = useRef(null);
  const ctaRef = useRef(null);
  
  // Refs para contenido animable
  const heroContentRef = useRef(null);
  const statsContentRef = useRef(null);
  const featuresContentRef = useRef(null);
  const ctaContentRef = useRef(null);
  
  // Hero con fondo sólido, transparente el navbar
  const heroBg = modoOscuro ? 'bg-black' : 'bg-white';
  const contentBg = modoOscuro ? 'bg-black' : 'bg-white';
  const box = modoOscuro ? 'bg-gray-900/90 border-gray-700' : 'bg-white/90 border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';

  // Colores específicos para hero según modo (sobre Aurora)
  const heroText = modoOscuro ? 'text-white' : 'text-black';
  const heroTextSec = modoOscuro ? 'text-white/90' : 'text-black/90';
  const heroTextMuted = modoOscuro ? 'text-white/80' : 'text-black/80';
  const heroBtnPrimary = modoOscuro 
    ? 'bg-white text-[#004aad] border-white hover:bg-[#00357a] hover:text-white hover:border-[#00357a]' 
    : 'bg-black text-white border-black hover:bg-gray-800 hover:text-white';
  const heroBtnSecondary = modoOscuro
    ? 'border-2 border-white text-white hover:bg-white hover:text-[#004aad]'
    : 'border-2 border-black text-black hover:bg-black hover:text-white';

  // Animaciones GSAP al montar el componente
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      
      // Badge - fade in + slide down
      tl.fromTo('.hero-badge',
        { opacity: 0, y: -30 },
        { opacity: 1, y: 0, duration: 0.6 }
      );
      
      // Título - fade in + slide up
      tl.fromTo('.hero-title',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8 },
        '-=0.3'
      );
      
      // Descripción
      tl.fromTo('.hero-desc',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6 },
        '-=0.4'
      );
      
      // Botones CTA
      tl.fromTo('.hero-cta',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6 },
        '-=0.3'
      );
      
      tl.fromTo('.hero-check > *',
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.35, stagger: 0.08 },
        '-=0.3'
      );

      tl.fromTo('.hero-showcase',
        { opacity: 0, y: 40, scale: 0.97 },
        { opacity: 1, y: 0, scale: 1, duration: 0.8 },
        '-=0.5'
      );
    }, heroRef);
    
    return () => ctx.revert();
  }, []);

  // Scroll suave entre secciones
  useEffect(() => {
    const sections = [heroRef, statsRef, featuresRef, ctaRef];
    const currentSectionRef = { current: 0 };
    const isScrollingRef = { current: false };
    const scrollDuration = 1.2;
    const scrollEase = 'power2.inOut';
    
    // Wheel snap solo en pointer fino (mouse/trackpad). En touch el scroll es nativo.
    const canWheelSnap = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)').matches;

    // Definir scrollToSection primero
    const scrollToSection = (index) => {
      if (index < 0 || index >= sections.length || isScrollingRef.current) return;
      
      isScrollingRef.current = true;
      const targetRef = sections[index];
      if (!targetRef.current) {
        isScrollingRef.current = false;
        return;
      }

      const container = document.getElementById('main-scroll');
      if (!container) {
        isScrollingRef.current = false;
        return;
      }

      const targetPosition = index === 0 ? 0 : targetRef.current.offsetTop;
      
      gsap.to(container, {
        scrollTop: targetPosition,
        duration: scrollDuration,
        ease: scrollEase,
        onComplete: () => {
          isScrollingRef.current = false;
          currentSectionRef.current = index;
          setActiveSection(index);
          window.__landingActiveSection = index;
        }
      });
    };

    // Exponer referencias para botones externos (después de definir scrollToSection)
    window.__landingScroll = {
      goToSection: scrollToSection,
      goToHero: () => {
        if (isScrollingRef.current) return;
        isScrollingRef.current = true;
        
        const container = document.getElementById('main-scroll');
        if (!container || !heroRef.current) {
          isScrollingRef.current = false;
          return;
        }

        gsap.to(container, {
          scrollTop: 0,
          duration: scrollDuration,
          ease: scrollEase,
          onComplete: () => {
            isScrollingRef.current = false;
            currentSectionRef.current = 0;
            setActiveSection(0);
            window.__landingActiveSection = 0;
          }
        });
      }
    };

    const handleWheel = (e) => {
      e.preventDefault();
      
      if (isScrollingRef.current) return;

      const delta = e.deltaY;
      const direction = delta > 0 ? 1 : -1;
      const nextSection = currentSectionRef.current + direction;

      if (nextSection >= 0 && nextSection < sections.length) {
        scrollToSection(nextSection);
      }
    };

    // Detectar sección activa basado en scroll nativo (para mobile y scroll no controlado)
    const handleScroll = () => {
      if (isScrollingRef.current) return;
      
      const container = document.getElementById('main-scroll');
      if (!container) return;

      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      
      sections.forEach((ref, index) => {
        if (!ref.current) return;
        
        if (index === 0) {
          if (scrollTop < containerHeight / 2) {
            if (currentSectionRef.current !== 0) {
              currentSectionRef.current = 0;
              setActiveSection(0);
              window.__landingActiveSection = 0;
            }
          }
          return;
        }
        
        const rect = ref.current.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementTop = rect.top - containerRect.top;
        
        // Si el elemento está en el viewport y es el más visible
        if (elementTop >= -containerHeight / 2 && elementTop < containerHeight / 2) {
          if (currentSectionRef.current !== index) {
            currentSectionRef.current = index;
            setActiveSection(index);
            window.__landingActiveSection = index;
          }
        }
      });
    };

    const container = document.getElementById('main-scroll');
    
    if (container && canWheelSnap) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('scroll', handleScroll);
      }
      delete window.__landingScroll;
    };
  }, []);

  // Sin animaciones de contenido al cambiar de sección (eliminadas para evitar interferencias)

  /** Sección 3 — beneficios concretos, sin jerga (detalle en /funcionalidades). */
  const features = [
    {
      icon: <FiDollarSign />,
      title: 'Cobrar lo que te deben',
      description:
        'Una lista clara de pendientes. Recordá por WhatsApp o compartí un link para que te paguen, sin ir cliente por cliente.',
    },
    {
      icon: <FiFileText />,
      title: 'Anotar ventas y gastos',
      description:
        'Cargás en segundos lo que vendiste y en qué gastaste. Podés escanear un comprobante si te resulta más fácil.',
    },
    {
      icon: <FiShield />,
      title: 'Mandar el mes al contador',
      description:
        'Cuando cierra el mes, juntás ventas, gastos y lo que quedó impago en un envío. Tu estudio deja de pedirte “todo otra vez”.',
    },
    {
      icon: <FiTrendingUp />,
      title: 'Ver cómo va el negocio',
      description:
        'Comparás un mes con otro: si entró más plata, si alguien se atrasó mucho, si los gastos se te fueron de las manos.',
    },
  ];

  /** Sección 2 — copy pensado para quien no es “de números” (hero se rediseña al final). */
  const growthPoints = [
    {
      icon: FiDollarSign,
      title: 'Sabés a quién cobrarle',
      text: 'Te mostramos quién te debe y cuánto, para que no pierdas el día revisando chats y papeles.',
    },
    {
      icon: FiShield,
      title: 'Tu contador recibe el mes ordenado',
      text: 'Ventas, gastos y pendientes en un solo envío. Menos “mandame todo de nuevo”.',
    },
    {
      icon: FiActivity,
      title: 'Ves si el mes cierra bien',
      text: 'Entró más de lo que salió. Alguien se atrasó. Lo ves claro, sin armar otra planilla.',
    },
    {
      icon: FiTrendingUp,
      title: 'Empezás en minutos',
      text: 'Desde el navegador o el celular. Sin programas complicados ni manuales de ERP.',
    },
  ];

  const growthFacts = [
    { value: '30', label: 'días para probar' },
    { value: '1', label: 'lugar para tu negocio' },
    { value: '✓', label: 'recordatorios de cobro' },
    { value: '✓', label: 'listo para el contador' },
  ];

  const heroChecks = [
    'Lista clara de quién te debe y cuánto',
    'Ventas y gastos en el mismo lugar',
    '30 días para probar, sin complicarte',
  ];

  /** Sección 4 — cierre invitador. */
  const ctaReassurance = [
    '30 días para probar sin presión',
    'En el navegador o como app de escritorio en tu PC',
    'Pensado para negocios chicos en Argentina',
  ];

  return (
    <MarketingLayout showNavbar={showNavbar}>
      {/* Hero */}
      <section ref={heroRef} className={`relative min-h-[100svh] lg:h-screen overflow-visible lg:overflow-hidden flex flex-col justify-start lg:justify-center py-8 sm:py-12 lg:py-0 ${heroBg}`}>
        <Spotlight opacity={0.65} />
        <div ref={heroContentRef} className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 lg:pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 xl:gap-16 items-center">
            <div className="lg:col-span-6 text-center lg:text-left space-y-4 sm:space-y-5">
              <p className={`hero-badge text-[11px] font-semibold tracking-[0.18em] uppercase ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                Para negocios chicos en Argentina
              </p>

              <h1 className={`hero-title text-3xl sm:text-4xl md:text-5xl xl:text-[3.25rem] font-extrabold leading-[1.08] tracking-tight ${heroText}`}>
                Sabé qué te deben y cerrá el mes sin otra planilla.
              </h1>

              <p className={`hero-desc text-sm sm:text-base lg:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0 ${heroTextSec}`}>
                Anotás ventas y gastos, ves quién te debe y mandás el resumen a tu contador. Todo desde el celu o la compu.
              </p>

              <ul className="hero-check space-y-2 max-w-md mx-auto lg:mx-0 text-left">
                {heroChecks.map((item) => (
                  <li key={item} className={`flex items-start gap-2.5 text-sm ${heroTextMuted}`}>
                    <FiCheckCircle className={`w-4 h-4 mt-0.5 shrink-0 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`} />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="hero-cta flex flex-col sm:flex-row gap-2.5 justify-center lg:justify-start pt-1">
                <Link
                  to="/register"
                  className={`group px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 ${heroBtnPrimary}`}
                >
                  Empezar gratis
                  <FiArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/funcionalidades"
                  className={`group px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 active:scale-95 ${heroBtnSecondary}`}
                >
                  <FiPlay className="inline-block mr-2 w-4 h-4" />
                  Ver cómo funciona
                </Link>
              </div>
              <DesktopAppInstallCta variant="hero" modoOscuro={modoOscuro} />
            </div>

            {!isLg && (
              <div className="w-full max-w-md mx-auto">
                <ProductPreview variant="compact" idPrefix="landing-mobile" />
              </div>
            )}

            {isLg && (
              <div className="hero-showcase lg:col-span-6 w-full max-w-lg mx-auto lg:max-w-none">
                <ProductPreview variant="full" idPrefix="landing-desktop" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Sección 2 — valor claro */}
      <section ref={statsRef} className={`relative py-16 lg:py-0 lg:h-screen lg:flex lg:items-center lg:justify-center ${contentBg}`}>
        <div ref={statsContentRef} className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-5">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-4 ${modoOscuro ? 'bg-[#004AAD]/20 text-[#5CA3FF]' : 'bg-[#004AAD]/10 text-[#004AAD]'}`}>
                <FiTrendingUp className="w-3.5 h-3.5" />
                <span>Hecho para PyMEs como la tuya</span>
              </div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-3 leading-tight ${textPri}`}>
                El mes deja de ser un enigma
              </h2>
              <p className={`text-sm md:text-base leading-relaxed mb-6 ${textSec}`}>
                Vendés, gastás y a veces te demoran el pago. Contsuite te ayuda a ver el panorama: qué entró, qué salió y a quién conviene recordarle hoy.
              </p>

              <SpotlightCard className={`rounded-xl p-4 space-y-3 border shadow-sm ${box}`}>
                <div>
                  <p className={`text-[11px] font-semibold uppercase tracking-wide ${textMuted}`}>Sin Contsuite</p>
                  <p className={`text-sm mt-1 ${textSec}`}>
                    “¿Cuánto me deben?” “¿Ya pagué el proveedor?” Todo repartido entre WhatsApp y una planilla que siempre queda atrás.
                  </p>
                </div>
                <div className={`h-px ${modoOscuro ? 'bg-gray-700' : 'bg-gray-200'}`} />
                <div>
                  <p className={`text-[11px] font-semibold uppercase tracking-wide text-[#1F80FF]`}>Con Contsuite</p>
                  <p className={`text-sm mt-1 ${textSec}`}>
                    Un solo lugar para clientes, ventas y gastos. Cerrás el mes con números que podés explicar en dos minutos.
                  </p>
                </div>
              </SpotlightCard>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/register"
                  className={`group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${modoOscuro ? 'bg-white text-black hover:bg-gray-200' : 'bg-gray-900 text-white hover:bg-black'}`}
                >
                  Empezar a registrar
                  <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/planes"
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${modoOscuro ? 'border-gray-600 text-white hover:bg-gray-800' : 'border-gray-300 text-gray-800 hover:bg-gray-50'}`}
                >
                  Ver planes
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {growthPoints.map(({ icon: Icon, title, text }) => (
                  <SpotlightCard key={title} className={`${box} h-full rounded-xl p-4 sm:p-5 border shadow-sm`}>
                    <div className={`w-9 h-9 rounded-lg mb-3 flex items-center justify-center ${modoOscuro ? 'bg-[#004AAD]/30 text-[#5CA3FF]' : 'bg-[#004AAD]/10 text-[#004AAD]'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <h3 className={`text-sm font-semibold mb-1.5 ${textPri}`}>{title}</h3>
                    <p className={`text-xs sm:text-sm leading-relaxed ${textSec}`}>{text}</p>
                  </SpotlightCard>
                ))}
              </div>

              <SpotlightCard className={`mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-xl border p-3 shadow-sm ${box}`}>
                {growthFacts.map((fact) => (
                  <div key={fact.label} className="text-center px-1 py-1.5">
                    <div className="text-lg font-semibold tabular-nums text-[#1F80FF]">{fact.value}</div>
                    <div className={`text-[11px] ${textMuted}`}>{fact.label}</div>
                  </div>
                ))}
              </SpotlightCard>
            </div>
          </div>
        </div>

        {/* Botón volver arriba - solo desktop */}
        <button
          onClick={() => window.__landingScroll?.goToHero()}
          className={`hidden lg:flex absolute bottom-8 right-8 w-12 h-12 rounded-full backdrop-blur-sm border items-center justify-center transition-all duration-300 group z-20 ${modoOscuro ? 'bg-white/10 border-white/20 hover:bg-white/20' : 'bg-black/10 border-black/20 hover:bg-black/20'}`}
          aria-label="Volver arriba"
        >
          <FiArrowRight className={`w-5 h-5 rotate-[-90deg] group-hover:-translate-y-1 transition-transform ${modoOscuro ? 'text-white' : 'text-gray-900'}`} />
        </button>
      </section>

      {/* Sección 3 — qué hace Contsuite en el día a día */}
      <section ref={featuresRef} className={`relative py-16 lg:py-0 lg:h-screen lg:flex lg:items-center lg:justify-center overflow-hidden ${contentBg}`}>
        <div ref={featuresContentRef} className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-10">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-4 ${modoOscuro ? 'bg-[#004AAD]/20 text-[#5CA3FF]' : 'bg-[#004AAD]/10 text-[#004AAD]'}`}>
              <FiZap className="w-3.5 h-3.5" />
              <span>Qué podés hacer acá</span>
            </div>
            <h2 className={`text-3xl md:text-4xl font-bold mb-3 leading-tight ${textPri}`}>
              Cuatro cosas que hoy te roban tiempo
            </h2>
            <p className={`text-sm md:text-base max-w-xl mx-auto leading-relaxed ${textSec}`}>
              Si vendés, cobrás y tenés gastos todos los meses, no necesitás ser contador para usarlo.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
            {features.map((feature) => (
              <SpotlightCard
                key={feature.title}
                className={`${box} h-full rounded-2xl p-5 lg:p-6 border shadow-sm`}
              >
                <div className={`w-11 h-11 rounded-xl mb-4 flex items-center justify-center ${modoOscuro ? 'bg-[#004AAD]/30 text-[#5CA3FF]' : 'bg-[#004AAD]/10 text-[#004AAD]'}`}>
                  {React.cloneElement(feature.icon, { className: 'w-5 h-5' })}
                </div>
                <h3 className={`text-base lg:text-lg font-bold mb-2 ${textPri}`}>
                  {feature.title}
                </h3>
                <p className={`text-sm leading-relaxed ${textSec}`}>
                  {feature.description}
                </p>
              </SpotlightCard>
            ))}
          </div>

          <p className={`text-center mt-8 text-sm ${textMuted}`}>
            ¿Querés el detalle técnico?{' '}
            <Link
              to="/funcionalidades"
              className={`font-semibold underline underline-offset-2 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}
            >
              Ver todas las funcionalidades
            </Link>
          </p>
        </div>
        
        {/* Botón volver arriba - solo desktop */}
        <button
          onClick={() => window.__landingScroll?.goToHero()}
          className={`hidden lg:flex absolute bottom-8 right-8 w-12 h-12 rounded-full backdrop-blur-sm border items-center justify-center transition-all duration-300 group z-20 ${modoOscuro ? 'bg-white/10 border-white/20 hover:bg-white/20' : 'bg-black/10 border-black/20 hover:bg-black/20'}`}
          aria-label="Volver arriba"
        >
          <FiArrowRight className={`w-5 h-5 rotate-[-90deg] group-hover:-translate-y-1 transition-transform ${modoOscuro ? 'text-white' : 'text-gray-900'}`} />
        </button>
      </section>

      {/* Sección 4 — invitación a probar */}
      <section ref={ctaRef} className="relative py-16 lg:py-0 lg:h-screen lg:flex lg:items-center lg:justify-center bg-[#004AAD]">
        <div ref={ctaContentRef} className="max-w-2xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-white/70 mb-3">
            El siguiente paso
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 leading-tight">
            Ordená tu mes y probalo con calma
          </h2>
          <p className="text-sm md:text-base text-white/90 mb-6 leading-relaxed max-w-lg mx-auto">
            Creá tu cuenta, cargá un cliente y anotá tu primera venta o gasto. En pocos minutos vas a ver si te ordena la cabeza.
          </p>
          <ul className="space-y-2.5 mb-8 max-w-md mx-auto text-left">
            {ctaReassurance.map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-sm text-white/95">
                <FiCheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-white" aria-hidden />
                {line}
              </li>
            ))}
          </ul>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              className="group inline-flex items-center justify-center gap-2 bg-white text-[#004aad] px-6 py-3 rounded-xl font-semibold text-sm border border-white hover:bg-[#00357a] hover:text-white transition-colors"
            >
              Crear cuenta gratis
              <FiArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/planes"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold text-sm border border-white/80 text-white hover:bg-white hover:text-[#004aad] transition-colors"
            >
              Ver planes y precios
            </Link>
          </div>
          <DesktopAppInstallCta variant="onBlue" />
          <p className="mt-6 text-xs text-white/75">
            ¿Preferís hablar antes?{' '}
            <Link to="/contacto" className="font-semibold text-white underline underline-offset-2 hover:text-white/90">
              Escribinos
            </Link>
          </p>
        </div>
        {/* Botón volver arriba - solo desktop */}
        <button
          onClick={() => window.__landingScroll?.goToHero()}
          className={`hidden lg:flex absolute bottom-8 right-8 w-12 h-12 rounded-full backdrop-blur-sm border items-center justify-center transition-all duration-300 group z-20 ${modoOscuro ? 'bg-white/10 border-white/20 hover:bg-white/20' : 'bg-black/10 border-black/20 hover:bg-black/20'}`}
          aria-label="Volver arriba"
        >
          <FiArrowRight className={`w-5 h-5 rotate-[-90deg] group-hover:-translate-y-1 transition-transform ${modoOscuro ? 'text-white' : 'text-gray-900'}`} />
        </button>
      </section>
    </MarketingLayout>
  );
};

export default Landing;
